import React from 'react';

// station-menu-gallery: the station page's nav links, as a pure function of the
// station's network.
//
// Extracted from ChatInterface so this list can be asserted directly. Rendering
// ChatInterface in a test means standing up the whole chat app — WS middleware,
// redux, the card pipeline — so in practice the station's nav had no coverage at
// all, and an entry could be dropped in a refactor without a single test going
// red. `PageTopbar`'s tabs get render-and-assert coverage because that component
// is cheap to mount; this is the same guarantee bought a different way.
//
// The array is handed to AuthHeader's `links`, which renders each `label` as JSX
// (hence the fragment on the network entry) and each `href` as a plain anchor.

// The Gallery lives at a fixed path, and the dashboard family links to the same
// one (PageTopbar TABS). Named so the two can't drift apart silently.
export const GALLERY_HREF = '/gallery';
// Same reasoning as GALLERY_HREF — PageTopbar's tabs import these, so the
// station menu and the dashboard cannot drift to different paths.
export const TASKS_HREF = '/tasks';
export const SCHEDULED_HREF = '/scheduled';
export const COMPANY_HREF = '/company';
// mailbox-menu: stations' messages to this person.
export const MESSAGES_HREF = '/messages';
// The Stations page — PageTopbar's tab and the main menu both point here.
export const STATIONS_HREF = '/dashboard';

// nav-menu-refresh: Tasks and Scheduled Tasks share one accordion in both menus.
// A link with `children` renders as a collapsible group (AuthHeader). `active`
// left undefined falls back to an exact path match.
export function tasksGroup({ tasksActive, scheduledActive } = {}) {
  return {
    key: 'tasks',
    label: 'Tasks',
    icon: 'tasks',
    children: [
      { label: 'Tasks', href: TASKS_HREF, active: tasksActive, icon: 'task' },
      { label: 'Scheduled Tasks', href: SCHEDULED_HREF, active: scheduledActive, icon: 'scheduled' },
    ],
  };
}

// The menu every page opens with: Stations, Networks, Gallery, Tasks, Messages. (Company left the
// menu; /company still serves the page.)
// `active` is the dashboard section key; the station page passes none.
export function buildMainNavLinks(active = null) {
  return [
    { label: 'Stations', href: STATIONS_HREF, active: active === 'find', icon: 'stations' },
    { label: 'Networks', href: '/dashboard/networks', active: active === 'network', icon: 'network' },
    { label: 'Gallery', href: GALLERY_HREF, active: active === 'gallery', icon: 'gallery' },
    tasksGroup({ tasksActive: active === 'tasks', scheduledActive: active === 'scheduled' }),
    // `badge: 'messages'` — AuthHeader shows the unread count beside it.
    { label: 'Messages', href: MESSAGES_HREF, active: active === 'messages', icon: 'messages', badge: 'messages' },
  ];
}

// The station page: the main menu, then this station's own section. AuthHeader
// appends Share link and Station settings after the last link, so they follow Network.
export function buildStationNavLinks(network) {
  return [
    ...buildMainNavLinks(),
    { divider: true, key: 'station-section' },
    {
      // The network name is bold; `label` is a node, not a string.
      label: network ? (<>Network: <strong>{network}</strong></>) : 'Network',
      // No network resolved yet (legacy numeric-first URL, or before the first
      // metrics frame stamps it) → the all-networks page.
      href: network
        ? `/dashboard/networks/${encodeURIComponent(network)}`
        : '/dashboard/networks',
      icon: 'network',
    },
  ];
}
